import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';
import { BrnPopoverComponent, BrnPopoverContentDirective, BrnPopoverTriggerDirective } from '@spartan-ng/brain/popover';
import { HlmPopoverContentDirective } from '@spartan-ng/helm/popover';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { HlmInputDirective } from '@spartan-ng/helm/input';
import { EXCLUDE_REASONS } from '../../models/campaign-workflow.model';

/** "Zurückstellen…": pick a preset reason or write one, then confirm. */
@Component({
  selector: 'app-exclude-reason',
  imports: [BrnPopoverComponent, BrnPopoverTriggerDirective, BrnPopoverContentDirective, HlmPopoverContentDirective, HlmButtonDirective, HlmInputDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.block]': 'fill()' },
  template: `
    <brn-popover sideOffset="6" align="end" [state]="state()" (stateChanged)="state.set($event)">
      @if (triggerClass(); as compact) {
        <button type="button" brnPopoverTrigger [class]="compact" [disabled]="disabled()">
          {{ label() }}
          @if (hint()) {
            <kbd class="rounded border border-border px-1 font-mono text-2xs text-muted-foreground">{{ hint() }}</kbd>
          }
        </button>
      } @else {
        <button hlmBtn type="button" variant="outline" brnPopoverTrigger [class.w-full]="fill()" [disabled]="disabled()">{{ label() }}</button>
      }
      <div hlmPopoverContent class="w-80 p-3" *brnPopoverContent="let ctx">
        <p class="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">Warum zurückstellen?</p>
        <div class="mt-2 space-y-0.5" role="radiogroup" aria-label="Grund">
          @for (preset of presets; track preset) {
            <button
              type="button"
              role="radio"
              [attr.aria-checked]="reason() === preset"
              class="flex h-9 w-full items-center rounded-md px-2 text-left text-sm transition-colors hover:bg-muted"
              [class.bg-muted]="reason() === preset"
              (click)="reason.set(preset)"
            >
              {{ preset }}
            </button>
          }
        </div>
        <input
          hlmInput
          class="mt-2 w-full"
          maxlength="500"
          placeholder="Anderer Grund"
          aria-label="Anderer Grund"
          [value]="presets.includes(reason()) ? '' : reason()"
          (input)="reason.set($any($event.target).value)"
          (keydown.enter)="confirm()"
        />
        <div class="mt-3 flex justify-end gap-2">
          <button hlmBtn type="button" variant="ghost" (click)="state.set('closed')">Abbrechen</button>
          <button hlmBtn type="button" [disabled]="!reason().trim()" (click)="confirm()">Zurückstellen</button>
        </div>
      </div>
    </brn-popover>
  `,
})
export class ExcludeReason {
  readonly label = input('Zurückstellen…');
  readonly disabled = input(false);
  /** Stretch the trigger to its container, e.g. in a button grid. */
  readonly fill = input(false);
  /** Classes for a compact trigger that matches surrounding actions, instead of a standard button. */
  readonly triggerClass = input<string | null>(null);
  /** Keyboard shortcut shown on the trigger. */
  readonly hint = input<string | null>(null);
  readonly confirmed = output<string>();
  readonly presets = EXCLUDE_REASONS;
  readonly state = signal<'open' | 'closed'>('closed');
  readonly reason = signal('');

  open(): void {
    this.state.set('open');
  }

  confirm(): void {
    const reason = this.reason().trim();
    if (!reason) return;
    this.state.set('closed');
    this.reason.set('');
    this.confirmed.emit(reason);
  }
}
